/**
 * Compliance Client schemas — the compliance-side mirror of CRM clients.
 *
 * Strategy A: created/updated by engagements route. `jurisdiction` is
 * mirrored from `engagement.jurisdiction` (engagement wins). `country` is
 * a 1Financial-side field (free-form or 'Other').
 */

import { z } from 'zod';
import { idSchema, iso8601Schema, emailSchema, phoneE164Schema, pageInput, booleanQuerySchema } from './common';
import { JURISDICTION_CODES } from '../reference/jurisdictions';
import { COUNTRY_CODES } from '../reference/jurisdictions';
import { clientStatusSchema } from './client';

export const complianceClientOutputSchema = z.object({
  id: idSchema,
  clientCode: z.string().regex(/^CLI-\d{5}$/),
  name: z.string().min(1).max(200),
  company: z.string().max(200).nullable(),
  contactName: z.string().max(120).nullable(),
  email: emailSchema.nullable(),
  phone: phoneE164Schema.nullable(),
  country: z.enum(COUNTRY_CODES as unknown as [string, ...string[]]).nullable(),
  jurisdiction: z.enum(JURISDICTION_CODES as unknown as [string, ...string[]]).nullable(),
  address: z.string().max(500).nullable(),
  engagementId: idSchema.nullable(),
  kickoffCompleted: z.boolean(),
  retainerWon: z.boolean(),
  kickoffCompletedAt: iso8601Schema.nullable(),
  retainerWonAt: iso8601Schema.nullable(),
  createdAt: iso8601Schema,
  updatedAt: iso8601Schema,
});
export type ComplianceClientOutput = z.infer<typeof complianceClientOutputSchema>;

export const createComplianceClientInputSchema = z.object({
  name: z.string().min(1).max(200),
  company: z.string().max(200).nullable().optional(),
  contactName: z.string().max(120).nullable().optional(),
  email: emailSchema.nullable().optional(),
  phone: phoneE164Schema.nullable().optional(),
  country: z.enum(COUNTRY_CODES as unknown as [string, ...string[]]).nullable().optional(),
  jurisdiction: z.enum(JURISDICTION_CODES as unknown as [string, ...string[]]).nullable().optional(),
  address: z.string().max(500).nullable().optional(),
  engagementId: idSchema.nullable().optional(),
});
export type CreateComplianceClientInput = z.infer<typeof createComplianceClientInputSchema>;

export const updateComplianceClientInputSchema = createComplianceClientInputSchema.partial();
export type UpdateComplianceClientInput = z.infer<typeof updateComplianceClientInputSchema>;

export const listComplianceClientsInputSchema = pageInput.extend({
  jurisdiction: z.enum(JURISDICTION_CODES as unknown as [string, ...string[]]).optional(),
  kickoffCompleted: z.boolean().optional(),
  retainerWon: z.boolean().optional(),
  /** "Other" = filter for non-standard country/jurisdiction values. */
  otherOnly: z.boolean().optional(),
  /** When true, includes soft-deleted (archived). Default false. */
  clientStatus: clientStatusSchema.optional(),
  all: booleanQuerySchema.optional(),
});
export type ListComplianceClientsInput = z.infer<typeof listComplianceClientsInputSchema>;

export const bulkDeleteComplianceClientsInputSchema = z.object({
  ids: z.array(idSchema).min(1).max(500),
  password: z.string().min(8),
});
export type BulkDeleteComplianceClientsInput = z.infer<typeof bulkDeleteComplianceClientsInputSchema>;